import { useState, useEffect, useMemo } from 'react';
import { Egreso } from '../../../shared/types';
import { EgresoMockRepository } from '../services/egreso-mock.repository';
import { useAuth } from '../../auth/hooks/useAuth';
import { LoggerService } from '../../../shared/services/logger.service';

export const useEgresosMock = () => {
  const [egresos, setEgresos] = useState<Egreso[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const repository = useMemo(() => new EgresoMockRepository(), []);

  useEffect(() => {
    if (!user) {
      setEgresos([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    LoggerService.debug('Subscribing to mock egresos', 'useEgresosMock', { userId: user.uid });

    const unsubscribe = repository.subscribeToEgresos(user.uid, (egresos) => {
      setEgresos(egresos);
      setLoading(false);
      LoggerService.debug('Mock egresos updated', 'useEgresosMock', { count: egresos.length });
    });

    return () => unsubscribe();
  }, [user, repository]);

  const createEgreso = async (egreso: Omit<Egreso, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!user) throw new Error('Usuario no autenticado');
    
    try {
      setError(null);
      await repository.create(user.uid, egreso);
      LoggerService.logUserAction('mock_egreso_created', 'useEgresosMock', { egreso });
    } catch (err: unknown) {
      const error = err as { message: string };
      setError(error.message);
      LoggerService.error('Mock egreso creation failed', 'useEgresosMock', { error });
      throw err;
    }
  };

  const updateEgreso = async (id: string, egreso: Partial<Omit<Egreso, 'id' | 'createdAt' | 'updatedAt'>>) => {
    if (!user) throw new Error('Usuario no autenticado');
    
    try {
      setError(null);
      await repository.update(user.uid, id, egreso);
      LoggerService.logUserAction('mock_egreso_updated', 'useEgresosMock', { id, egreso });
    } catch (err: unknown) {
      const error = err as { message: string };
      setError(error.message);
      LoggerService.error('Mock egreso update failed', 'useEgresosMock', { error });
      throw err;
    }
  };

  const deleteEgreso = async (id: string) => {
    if (!user) throw new Error('Usuario no autenticado');
    
    try {
      setError(null);
      await repository.delete(user.uid, id);
      LoggerService.logUserAction('mock_egreso_deleted', 'useEgresosMock', { id });
    } catch (err: unknown) {
      const error = err as { message: string };
      setError(error.message);
      LoggerService.error('Mock egreso deletion failed', 'useEgresosMock', { error });
      throw err;
    }
  };

  // Business logic methods
  const getEgresosByCategory = async (categoria: string): Promise<Egreso[]> => {
    if (!user) throw new Error('Usuario no autenticado');
    
    try {
      return await repository.getByCategory(user.uid, categoria);
    } catch (err: unknown) {
      const error = err as { message: string };
      LoggerService.error('Mock get by category failed', 'useEgresosMock', { error });
      throw new Error(error.message);
    }
  };

  const getEgresosByDateRange = async (startDate: Date, endDate: Date): Promise<Egreso[]> => {
    if (!user) throw new Error('Usuario no autenticado');
    
    try {
      return await repository.getByDateRange(user.uid, startDate, endDate);
    } catch (err: unknown) {
      const error = err as { message: string };
      LoggerService.error('Mock get by date range failed', 'useEgresosMock', { error });
      throw new Error(error.message);
    }
  };

  const getTotalEgresos = (): number => {
    return egresos.reduce((total, egreso) => total + egreso.monto, 0);
  };

  const getEgresosByMonth = (year: number, month: number): Egreso[] => {
    return egresos.filter(egreso => {
      const fecha = egreso.fecha;
      return fecha.getFullYear() === year && fecha.getMonth() === month;
    });
  };

  const getEgresosByYear = (year: number): Egreso[] => {
    return egresos.filter(egreso => egreso.fecha.getFullYear() === year);
  };

  const getCategories = (): string[] => {
    const categories = new Set(egresos.map(egreso => egreso.categoria));
    return Array.from(categories).sort();
  };

  const getCategoryTotals = (): { [categoria: string]: number } => {
    const totals: { [categoria: string]: number } = {};
    egresos.forEach(egreso => {
      totals[egreso.categoria] = (totals[egreso.categoria] || 0) + egreso.monto;
    });
    return totals;
  };

  return {
    egresos,
    loading,
    error,
    createEgreso,
    updateEgreso,
    deleteEgreso,
    getEgresosByCategory,
    getEgresosByDateRange,
    getTotalEgresos,
    getEgresosByMonth,
    getEgresosByYear,
    getCategories,
    getCategoryTotals,
  };
};
